import type TransportOrder      from "@/domains/TransportOrders/TransportOrder";
import SerializeSimplified      from "@/core/misc/decorators/SerializeSimplified";
import Title_ru_RU_Class        from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU              from "@/core/misc/decorators/Title_ru_RU";
import TYPES                    from '@/core/services/Changelogs/types';
import Core                     from '@/core/Core';
import AbstractShipmentDocument from "@/domains/Abstract/AbstractShipmentDocument";
import AssemblyPosition         from '@/domains/Assemblies/AssemblyPosition';
import DateSimple               from '@/core/types/DateSimple';
import Sum                      from "@/core/types/Sum";
import OrderPosition            from "@/domains/Orders/OrderPosition";
import IntRange                 from "@/core/types/IntRange";
import ValidationError          from '@/core/misc/ValidationError';

/* Причины по которым нельзя сделать то или иное действие */
const REASON_UNAVAILABLE = {
	TRANSPORT_ORDER:            'По этой сборке уже создан ЗТК',
	IS_SHIPPED:                 'По этой сборке уже совершена отгрузка',
	IS_SHIPPED_ENTIRELY:        'Эта сборка уже полностью отгружена',
	IS_OFFER_BASED:             'Сборочный лист создан на основе предложения',
	NO_OFFER_BASED:             'Сборочный лист создан не на основе предложения',
	NO_EXIST_ENTITY_ON_BACKEND: 'Сборочный лист ещё не создан',
	NOT_ALLOWED:                'Доступ ограничен',
} as const;

type ReasonResult = {
	hasReason: boolean;
	string: string | null;
}

@Title_ru_RU_Class('Сборочный лист')
class Assembly extends AbstractShipmentDocument {

	@Title_ru_RU('дата сборки', TYPES.DATE)
	assemblyDate: DateSimple;
	@Title_ru_RU('плановая дата получения', TYPES.BOOLEAN)
	plannedReceiptDate: DateSimple | null;

	/** Отгружен частично */
	isShipped: boolean;
	/** Отгружен полностью */
	isShippedEntirely: boolean;

	/** ЗТК, если есть */
	isTransportOrderCreated: boolean;
	@SerializeSimplified
	transportOrder: TransportOrder | { id: number };

	accountingCurrencyCode: string = Core.currentPartner.country.localCurrency.code || Core.config.localCurrency.code;

	static isAssembly(entity: unknown): entity is Assembly {
		return entity instanceof Assembly;
	}

	get documentClass(): string {
		return 'Assembly';
	}

	static fromServer(data: any, result = new Assembly()): Assembly {
		super.fromServer(data, result);

		result.assemblyDate       = DateSimple.fromServer(data.assemblyDate)!;
		result.plannedReceiptDate = DateSimple.fromServer(data.plannedReceiptDate);

		result.positions          = Core.utils.hydrateArray(data.positions, AssemblyPosition.fromServer);

		return result;
	}

	/**
	 * Закомментировано: из интерфейса создать сборку с нуля невозможно, она всегда
	 * собирается из позиций заказа (fromOrderPositions). Если сборку с нуля когда-нибудь
	 * понадобится создавать, сначала нужно научить addPosition инициализировать шапку
	 * документа по первой позиции — сейчас он её только сверяет.
	 */
	// static fromNew(result = new Assembly()): Assembly {
	// 	super.fromNew(result);
	//
	// 	return result;
	// }

	static fromOrderPosition(orderPosition: OrderPosition, result: Assembly = new Assembly()): Assembly {
		super.fromOrderPosition(orderPosition, result);
		result.isShipped = false;

		return result;
	}

	/**
	 * Сборка под позиции заказа.
	 *
	 * Реквизиты берутся из первой позиции: в один лист собирают позиции одной
	 * пары контрагентов, у остальных они те же. Позиции создаёт сама фабрика —
	 * снаружи собирать сборку по частям больше негде.
	 */
	static fromOrderPositions(orderPositions: OrderPosition[], result: Assembly = new Assembly()): Assembly {
		this.fromOrderPosition(orderPositions[0], result);

		for (const orderPosition of orderPositions) {
			result.addPosition(AssemblyPosition.fromOrderPosition(orderPosition));
		}

		return result;
	}

	calculateTemperatureRange(): void {
		if (this.positions.length === 0) {
			this.storeTemperatureRange = new IntRange();
			return;
		}

		const intervals = this.positions.map(
			(item: AssemblyPosition) => item.orderPosition.storeTemperatureRange,
		);

		if (intervals.length === 1) {
			this.storeTemperatureRange = Core.utils.hydrate({ ...intervals[0] }, IntRange.fromServer);
		} else {
			this.storeTemperatureRange = Core.utils.hydrate(Core.utils.number.numberIntervalIntersection(intervals), IntRange.fromServer);
		}
	}

	/**
	 * К проверкам совместимости из предка добавляется температурный режим:
	 * диапазоны хранения всех позиций сборки должны пересекаться.
	 */
	validatePosition(position: AssemblyPosition): void {
		super.validatePosition(position);

		const intervals = this.positions
			.concat([position])
			.map((item: AssemblyPosition) => item.orderPosition.storeTemperatureRange);

		const intersection = Core.utils.number.numberIntervalIntersection(intervals);

		if (intersection !== null && intersection.end - intersection.start < 0) {
			throw new ValidationError('Не совпадает температурный режим у позиции и сборки');
		}
	}

	addPosition(newPosition: AssemblyPosition): void {
		super.addPosition(newPosition);

		this.calculateTemperatureRange();
	}

	deletePosition(assemblyPosition: AssemblyPosition | null, index?: number): void {
		/** Если передана позиция — ищем её индекс в массиве */
		if (assemblyPosition) {
			/** Позиция ещё не сохранена на сервере — ищем по orderPosition.id */
			if (assemblyPosition.id === null) {
				index = this.positions.findIndex(
					position => position.orderPosition.id === assemblyPosition.orderPosition.id,
				);
			/** Позиция уже существует на сервере — ищем по собственному id */
			} else {
				index = this.positions.findIndex(
					position => position.id === assemblyPosition.id,
				);
			}
		}

		if (index === undefined || index === -1) {
			console.error('deletePosition: не удалось найти позицию для удаления');
			return;
		}

		this.positions.splice(index, 1);

		this.calculateTemperatureRange();
	}

	/** Закрыть заказ по всем позициям сборки («Установить для всех») */
	closeAllOrderPositions(): void {
		this.positions.forEach(assemblyPosition => assemblyPosition.closeOrderPosition());
	}

	get cargoUnitsString(): string {
		if (this.cargoUnits.size < 1) {
			return '--';
		}
		const place = Core.utils.regional.ru.pluralize(this.cargoUnitsCount.toNumber(), 'место', 'места', 'мест');
		return `${this.cargoUnitsCount?.formatAsInt()} ${place}, объем: ${this.cargoVolume.format()} м3, нетто: ${this.cargoWeightNet.format()} кг, брутто: ${this.cargoWeightGross.format()} кг`;
	}

	/* Используется в одном случае, где поиск по orderPosition действительно найдёт то что нам нужно */
	getAssemblyPositionIdByOrderPosition(orderPosition: { id: number }): number {
		const index = this.positions.findIndex(
			(_assemblyPosition: AssemblyPosition) =>
				_assemblyPosition.orderPosition.id === orderPosition.id,
		);
		if (index === -1) {
			console.error(
				'Assembly\nfunction getAssemblyPositionIdByOrderPosition\nnot found assemblyPosition',
			);
		}
		return index;
	}

	/**
	 * Объявленная стоимость груза одной суммой в валюте учёта.
	 * Сделано эксклюзивно для ЗТК.
	 *
	 * Документ основания может быть мультивалютным — каждую позицию
	 * конвертируем из её валюты в валюту учёта и складываем.
	 */
	async getDeclaredCost(): Promise<Sum> {
		let total = Sum.zero();

		for (const position of this.positions) {
			const currencyCode = position.orderPosition?.currencyCode;

			if (!currencyCode) { continue; }

			const converted = await Core.utils.money.convert(
				position.orderPosition.sumFullSeller,
				currencyCode,
				this.accountingCurrencyCode,
				this.createdAt || new Date(),
			);

			total = total.plus(converted);
		}

		return total;
	}

	// ============================================================
	// Причины недоступности действий
	// ============================================================

	/*
	 * Почему важно сделать именно так, потому что существует как минимум 2 места, где нужно
	 * следить за теми действиями, которые мы можем сделать с сущностью,
	 * соответственно, если есть причина не давать сделать действие, то в поле hasReason будет значение true
	 * а в string - сама причина
	 * Крч, чтобы код не дублировать, и мы захотим поправить условия, то поправим только тут
	 */
	static reasonUnavailableEdit(assembly: Assembly | null | undefined): ReasonResult {
		const result: ReasonResult = {
			hasReason: false,
			string:    null,
		};

		if (!assembly) {
			return result;
		}

		if (!(assembly.isShipped || assembly.isShippedEntirely)) {
			return result;
		}

		result.hasReason = true;
		result.string    = 'Не доступно\n';

		if (assembly.isShipped || assembly.isShippedEntirely) {
			result.string += `${REASON_UNAVAILABLE.IS_SHIPPED}\n`;
		}

		/* fixme выпилить если это условие не станет валидным */
		// if (assembly.isTransportOrderCreated) result.string += `${REASON_UNAVAILABLE.TRANSPORT_ORDER}\n`;
		return result;
	}

	static reasonUnavailableShipmentString(assembly: Assembly | null | undefined): ReasonResult {
		const result: ReasonResult = {
			hasReason: false,
			string:    null,
		};

		if (!assembly) {
			return result;
		}

		/**
		 * Было решено снять ранее существующие ограничения на создание сборок/отгрузок
		 * по роли пользователя
		 */
		if (assembly.id && !assembly.isShippedEntirely) {
			return result;
		}

		result.hasReason = true;
		result.string    = 'Не доступно\n';

		if (!assembly.id) {
			result.string += `${REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND}\n`;
		}
		if (assembly.isShippedEntirely) {
			result.string += `${REASON_UNAVAILABLE.IS_SHIPPED_ENTIRELY}\n`;
		}

		return result;
	}

	static reasonUnavailableCreateTransportOrderString(assembly: Assembly | null | undefined): string | null {
		if (!assembly) {
			return null;
		}

		if (!assembly.id) {
			return `${REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND}\n`;
		}
		if (assembly.isTransportOrderCreated) {
			return `${REASON_UNAVAILABLE.TRANSPORT_ORDER}\n`;
		}

		return null;
	}

	static reasonUnavailableDelete(assembly: Assembly | null | undefined): string | null {
		if (!assembly) {
			return null;
		}

		if (!assembly.id) {
			return `${REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND}\n`;
		}
		if (assembly.isShipped || assembly.isShippedEntirely) {
			return `${REASON_UNAVAILABLE.IS_SHIPPED}\n`;
		}
		if (assembly.isTransportOrderCreated) {
			return `${REASON_UNAVAILABLE.TRANSPORT_ORDER}\n`;
		}

		return null;
	}

	static reasonUnavailableHistory(assembly: Assembly | null | undefined): ReasonResult {
		const result: ReasonResult = {
			hasReason: false,
			string:    null,
		};

		if (!assembly) {
			return result;
		}

		const isSeller: boolean = Core.currentPartner?.isSeller!;

		if (!assembly.id || isSeller) {
			result.hasReason = true;
			result.string    = 'Не доступно\n';

			if (!assembly.id) {
				result.string += `${REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND}\n`;
			}
			/** access violation, смотреть историю может только покупатель */
			if (isSeller) {
				result.string += `${REASON_UNAVAILABLE.NOT_ALLOWED}`;
			}
		}

		return result;
	}
}

export default Assembly;
